import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Course } from '../types';
import { 
  Plus, BookOpen, Edit2, Eye, EyeOff, Trash2, 
  Clock, Users, Loader2, Sparkles 
} from 'lucide-react';

export default function TeacherDashboard() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const data = await api.getTeacherCourses();
      setCourses(data);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (courseId: number) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await api.deleteCourse(courseId);
      setCourses(prev => prev.filter(c => c.id !== courseId));
    } catch (error) {
      console.error('Failed to delete course:', error);
    }
  };

  const handleTogglePublish = async (course: Course) => {
    try {
      if (course.status === 'published') {
        await api.unpublishCourse(course.id);
      } else {
        await api.publishCourse(course.id);
      }
      loadCourses();
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-apple-blue animate-spin" />
          <p className="text-apple-text-secondary">Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-apple-text mb-2">Teacher Dashboard</h1>
          <p className="text-apple-text-secondary">
            Manage your courses and create new learning experiences
          </p>
        </div>
        <Link
          to="/teacher/create"
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Course
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-apple-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-apple-blue" />
            </div>
            <div>
              <p className="text-2xl font-bold text-apple-text">{courses.length}</p>
              <p className="text-sm text-apple-text-secondary">Total Courses</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-apple-sm flex items-center justify-center">
              <Eye className="w-5 h-5 text-apple-green" />
            </div>
            <div>
              <p className="text-2xl font-bold text-apple-text">
                {courses.filter(c => c.status === 'published').length}
              </p>
              <p className="text-sm text-apple-text-secondary">Published</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-apple-sm flex items-center justify-center">
              <Clock className="w-5 h-5 text-apple-orange" />
            </div>
            <div>
              <p className="text-2xl font-bold text-apple-text">
                {courses.filter(c => c.status === 'draft').length}
              </p>
              <p className="text-sm text-apple-text-secondary">In Draft</p>
            </div>
          </div>
        </div>
      </div>

      {/* Courses List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-apple-text">Your Courses</h2>
        
        {courses.length === 0 ? (
          <div className="card text-center py-16">
            <div className="w-16 h-16 bg-blue-100 rounded-apple-sm flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-apple-blue" />
            </div>
            <h3 className="text-lg font-medium text-apple-text mb-2">Create your first course</h3>
            <p className="text-apple-text-secondary mb-6 max-w-md mx-auto">
              Use our AI-powered tools to create comprehensive Data Science courses from simple outlines.
            </p>
            <Link to="/teacher/create" className="btn-primary inline-flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Get Started
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {courses.map((course) => (
              <div
                key={course.id}
                className="card flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-apple-sm flex items-center justify-center ${
                    course.status === 'published' 
                      ? 'bg-green-100' 
                      : 'bg-orange-100'
                  }`}>
                    <BookOpen className={`w-6 h-6 ${
                      course.status === 'published' 
                        ? 'text-apple-green' 
                        : 'text-apple-orange'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-apple-text text-lg">{course.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-apple-text-secondary">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        course.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {course.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                      <span>Created {new Date(course.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Link
                    to={`/teacher/edit/${course.id}`}
                    className="p-2 text-apple-text-secondary hover:text-apple-blue hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-5 h-5" />
                  </Link>
                  <button
                    onClick={() => handleTogglePublish(course)}
                    className={`p-2 rounded-lg transition-colors ${
                      course.status === 'published'
                        ? 'text-apple-green hover:bg-green-50'
                        : 'text-apple-text-secondary hover:text-apple-blue hover:bg-blue-50'
                    }`}
                    title={course.status === 'published' ? 'Unpublish' : 'Publish'}
                  >
                    {course.status === 'published' ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(course.id)}
                    className="p-2 text-apple-text-secondary hover:text-apple-red hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
